import React, { useState, useEffect, use } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import ChapterHeader from './ChapterHeader';
import { toast, Flip } from 'react-toastify';
import NoChapContent from './NoChapContent';
import ChapEditor from './ChapEditor';
import api from '@utils/axiosInstance';
import { setChapterLangList, setSelectedLanguage, setChapState, setChapterList } from '@modules/chapter';
import { setPlayTime, setSelectedPlayTime } from '@modules/media';
import '../../../../../../resources/assets/styles/chapter.scss';
import Loading from '../../../../_commons/LoadingComponent';

function ChapterTab() {
    const dispatch = useDispatch();
    const { uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const userToken = useSelector(state => state.organization.userToken);
    const organizationData = useSelector(state => state.organization.organizationData);

    const [availableChapterLanguages, setAvailableChapterLanguages] = useState([]); // 현재 영상에서 선택할 수 있는 모든 언어를 담은 배열
    const [currentSelectedLanguage, setCurrentSelectedLanguage] = useState({}); // 현재 탭에서 선택된 언어
    const [isAddModalOpen, setIsAddModalOpen] = useState(false); // 자막 추가 모달 열기
    const [showNoChap, setShowNoChap] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    // 초기화
    useEffect(() => {
        dispatch(setPlayTime(0));
        dispatch(setSelectedPlayTime(0));
        dispatch(setChapterLangList({}));
        dispatch(setChapterList({}));
        dispatch(setChapState([]));
        dispatch(setSelectedLanguage({}));
    }, [dispatch]);


    const getLangFromPath = () => {
        const pathSegments = location.pathname.split('/');
        const chapterIndex = pathSegments.findIndex(segment => segment === 'chapter');

        return (chapterIndex !== -1 && pathSegments.length > chapterIndex + 1)
            ? pathSegments[chapterIndex + 1]
            : null;
    };

    const navigateWithoutLang = () => {
        const pathSegments = location.pathname.split('/');
        const chapterIndex = pathSegments.findIndex(segment => segment === 'chapter');

        if (chapterIndex !== -1) {
            const newPath = pathSegments.slice(0, chapterIndex + 1).join('/')
            navigate(newPath, { replace: true });
        }
    };

    const navigateWithLang = (lang) => {
        const pathSegments = location.pathname.split('/');
        const chapterIndex = pathSegments.findIndex(segment => segment === 'chapter');

        if (chapterIndex !== -1) {
            const newPath = pathSegments.slice(0, chapterIndex + 1).join('/') + '/' + lang;
            navigate(newPath, { replace: true });
        }
    };


    useEffect(() => {
        api({
            method: 'post',
            url: '/csmsmedia/chapter/getState',
            data: {
                uuid,
                type: 'ALL'
            }
        })
            .then(response => {
                setIsLoading(false);

                if (response.data.code === 100) {
                    const chapterLangs = response.data.data.chapter;

                    if (chapterLangs.length > 0) {
                        setAvailableChapterLanguages(chapterLangs);
                        dispatch(setChapterLangList(chapterLangs));
                    } else {
                        setAvailableChapterLanguages([]);
                        setCurrentSelectedLanguage({});
                        setShowNoChap(true);
                        navigateWithoutLang();
                        return;
                    }

                    const langFromPath = getLangFromPath();
                    console.log('langFromPath', langFromPath);

                    if (langFromPath) {
                        const matchingLanguage = chapterLangs.find(clang => clang.lang === langFromPath);

                        if (!matchingLanguage) {
                            toast.error(t('vod.manage.invalid_language_access'), {
                                position: "top-center",
                                transition: Flip
                            });
                            setCurrentSelectedLanguage(chapterLangs[0]);
                            dispatch(setSelectedLanguage(chapterLangs[0]));
                            navigateWithLang(chapterLangs[0].lang);
                            setShowNoChap(false);
                        } else {
                            setCurrentSelectedLanguage(matchingLanguage);
                            dispatch(setSelectedLanguage(matchingLanguage));
                            setShowNoChap(false);
                        }
                    } else {
                        setCurrentSelectedLanguage(chapterLangs[0]);
                        dispatch(setSelectedLanguage(chapterLangs[0]));
                        setShowNoChap(false);
                        navigateWithLang(chapterLangs[0].lang);
                    }
                }
            })
            .catch(err => {
                console.error('Chapter load error', err);
                setAvailableChapterLanguages([]);
                setIsLoading(false);
            });
    }, [uuid, userToken, organizationData]);

    useEffect(() => { // url에서 직접 언어 변경했을 때 처리
        if (availableChapterLanguages.length > 0) {
            const langFromPath = getLangFromPath();
            console.log('langFromPath', langFromPath);
            if (langFromPath) { // URL에 언어가 있는 경우
                const matchingLanguage = availableChapterLanguages.find(clang => clang.lang === langFromPath);
                if (!matchingLanguage) { // 안맞는 언어로 접근한 경우
                    toast.error(t('vod.manage.invalid_language_access'), {
                        position: "top-center",
                        transition: Flip
                    });
                    setCurrentSelectedLanguage(availableChapterLanguages[0]);
                    dispatch(setSelectedLanguage(availableChapterLanguages[0]));
                    navigateWithLang(availableChapterLanguages[0].lang);
                    setShowNoChap(false);
                } else { // 맞는 언어로 접근한 경우
                    setCurrentSelectedLanguage(matchingLanguage);
                    dispatch(setSelectedLanguage(matchingLanguage));
                    setShowNoChap(false);
                }
            } else { // URL에 언어가 없는 경우
                if (availableChapterLanguages.length > 0) { // 생성된 언어가 있는 경우
                    setCurrentSelectedLanguage(availableChapterLanguages[0]);
                    dispatch(setSelectedLanguage(availableChapterLanguages[0]));
                    setShowNoChap(false);
                    navigateWithLang(availableChapterLanguages[0].lang);
                } else { // 생성된 언어가 없는 경우
                    setCurrentSelectedLanguage({});
                    dispatch(setSelectedLanguage({}));
                    setShowNoChap(true);
                }
            }
        }
    }, [location.pathname, availableChapterLanguages]);

    return (
        <div className='tab-video-chapter'>
            {isLoading ? (
                <Loading />
            ) : !showNoChap ? (
                <div className='wrapper-chapter-content'>
                    <ChapterHeader
                        isAddModalOpen={isAddModalOpen}
                        setIsAddModalOpen={setIsAddModalOpen}
                        currentSelectedLanguage={currentSelectedLanguage}
                        setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                        availableChapterLanguages={availableChapterLanguages}
                        setAvailableChapterLanguages={setAvailableChapterLanguages}
                        isLoading={isLoading}
                        setShowNoChap={setShowNoChap}
                    />
                    {availableChapterLanguages.length > 0 && (
                        <ChapEditor />
                    )}
                </div>
            ) : (
                <div className='no-chap-content-wrapper'>
                    <NoChapContent
                        availableChapterLanguages={availableChapterLanguages}
                        setAvailableChapterLanguages={setAvailableChapterLanguages}
                        setCurrentSelectedLanguage={setCurrentSelectedLanguage}
                        setIsAddModalOpen={setIsAddModalOpen}
                        setShowNoChap={setShowNoChap}
                    />
                </div>
            )}
        </div>
    );
}

export default ChapterTab;
